import React, { Fragment, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Media,
  Row,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { sentenceCase, titleCase } from "../../utils/case";
import { getVenueById, updateVenue } from "../../api/venue.api";
import { getDeviceSelections } from "../../api/device.api";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  EditorState,
  convertFromHTML,
  ContentState,
  convertToRaw,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import { generateTimeOptions, to12Hour } from "../../utils/datetime";

const VenueEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );

  const [form, setForm] = useState({
    name: "",
    description: "",
    remark: "",
    requiresPayment: false,
    requiresApproval: false,
    allowPublicBooking: false,
    cost: "",
    peakCost: "",
    chargedBy: "",
    photo: null,
    existingPhoto: null,

    minLeadTimeValue: "",
    minLeadTimeType: "",
    maxLeadTimeValue: "",
    maxLeadTimeType: "",

    operatingHours: [{ day: "", startTime: "", endTime: "" }],
    deviceIds: [],
  });

  // ================= FETCH DEVICES =================
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await getDeviceSelections();
        setDevices(res.data || []);
      } catch (err) {
        toast.error("Failed to load devices");
      }
    };

    fetchDevices();
  }, []);

  // ================= FETCH VENUE =================
  const fetchVenue = async () => {
    try {
      const data = await getVenueById(id);

      const blocksFromHTML = convertFromHTML(data.remark || "");
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );

      setEditorState(EditorState.createWithContent(contentState));

      setForm({
        name: data.name || "",
        description: data.description || "",
        remark: data.remark || "",
        requiresPayment: data.requiresPayment || false,
        requiresApproval: data.requiresApproval || false,
        allowPublicBooking: data.allowPublicBooking || false,
        cost: data.cost || "",
        peakCost: data.peakCost || "",
        chargedBy: data.chargedBy || "",
        photo: null,
        existingPhoto: data.photos || null,

        minLeadTimeValue: data.minLeadTime?.value || "",
        minLeadTimeType: data.minLeadTime?.type || "",
        maxLeadTimeValue: data.maxLeadTime?.value || "",
        maxLeadTimeType: data.maxLeadTime?.type || "",

        operatingHours:
          data.operatingHours?.length > 0
            ? data.operatingHours.map((oh) => ({
                day: oh.day,
                startTime: oh.startTime?.slice(0, 5), // "09:00:00" → "09:00"
                endTime: oh.endTime?.slice(0, 5),
              }))
            : [{ day: "", startTime: "", endTime: "" }],

        deviceIds: data.devices?.map((d) => d.id) || [],
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchVenue();
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleDevice = (deviceId) => {
    setForm((prev) => {
      const exists = prev.deviceIds.includes(deviceId);
      return {
        ...prev,
        deviceIds: exists
          ? prev.deviceIds.filter((id) => id !== deviceId)
          : [...prev.deviceIds, deviceId],
      };
    });
  };

  const onEditorStateChange = (state) => {
    setEditorState(state);
  };

  const handleOperatingHourChange = (index, field, value) => {
    const updated = [...form.operatingHours];
    updated[index][field] = value;
    setForm((prev) => ({ ...prev, operatingHours: updated }));
  };

  const addOperatingHour = () => {
    setForm((prev) => ({
      ...prev,
      operatingHours: [
        ...prev.operatingHours,
        { day: "", startTime: "", endTime: "" },
      ],
    }));
  };

  const removeOperatingHour = (index) => {
    const updated = form.operatingHours.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, operatingHours: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (form.requiresPayment) {
        if (!form.cost || !form.chargedBy) {
          return toast.error("Cost and charging type are required");
        }
      }

      const validOperatingHours = form.operatingHours.filter(
        (oh) => oh.day && oh.startTime && oh.endTime
      );

      const remarkHtml = draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      );

      const payload = {
        name: form.name,
        description: form.description,
        remark: remarkHtml,
        requiresPayment: form.requiresPayment,
        requiresApproval: form.requiresApproval,
        allowPublicBooking: form.allowPublicBooking,
        cost: form.cost ? Number(form.cost) : null,
        peakCost: form.peakCost ? Number(form.peakCost) : null,
        chargedBy: form.chargedBy,

        minLeadTime: form.minLeadTimeValue
          ? {
              value: Number(form.minLeadTimeValue),
              type: form.minLeadTimeType,
            }
          : null,

        maxLeadTime: form.maxLeadTimeValue
          ? {
              value: Number(form.maxLeadTimeValue),
              type: form.maxLeadTimeType,
            }
          : null,

        operatingHours: validOperatingHours,
        deviceIds: form.deviceIds,
        photo: form.photo,
      };

      setLoading(true);
      await updateVenue(id, payload);

      toast.success("Venue updated successfully");
      navigate(`/venues/view/${id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const triggerSubmit = () => {
    if (formRef.current) formRef.current.requestSubmit();
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("venue"))}
        title={titleCase(t("edit venue"))}
        mainTitle={titleCase(t("edit venue"))}
      />

      <Container fluid>
        <Row>
          <Col>
            {/* ================= BASIC INFO ================= */}
            <Card className="mb-4">
              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col md="3">
                      <div
                        style={{
                          border: "1px dashed #ccc",
                          height: "120px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {form.existingPhoto ? (
                          <img
                            src={`https://files.aimpointventures.my/${form.existingPhoto}?v=${Date.now()}`}
                            alt="venue"
                            style={{ maxWidth: "100%", maxHeight: "100%" }}
                          />
                        ) : (
                          <span>{titleCase(t("no image"))}</span>
                        )}
                      </div>
                    </Col>

                    <Col md="9" className="d-flex align-items-center">
                      <FormGroup className="mb-0 w-100">
                        <Label>{titleCase(t("change image"))}</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            handleChange("photo", e.target.files[0])
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>{titleCase(t("venue name"))}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("description"))}</Label>
                    <Input
                      type="textarea"
                      value={form.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                    />
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            {/* ================= COSTS ================= */}
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("costs"))}</H5>
              </CardHeader>
              <CardBody>
                <Media className="mb-3 align-items-center">
                  <Label className="switch m-r-10 mb-0">
                    <Input
                      type="checkbox"
                      checked={form.requiresPayment}
                      onChange={(e) =>
                        handleChange("requiresPayment", e.target.checked)
                      }
                    />
                    <span className="switch-state"></span>
                  </Label>
                  <Label>{titleCase(t("requires payment"))}</Label>
                </Media>

                {form.requiresPayment && (
                  <>
                    <FormGroup>
                      <Label>{titleCase(t("default cost"))}</Label>
                      <Input
                        type="number"
                        value={form.cost}
                        onChange={(e) =>
                          handleChange("cost", e.target.value)
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>{titleCase(t("peak cost"))}</Label>
                      <Input
                        type="number"
                        value={form.peakCost}
                        onChange={(e) =>
                          handleChange("peakCost", e.target.value)
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>{titleCase(t("charged by"))}</Label>
                      <Input
                        type="select"
                        value={form.chargedBy}
                        onChange={(e) =>
                          handleChange("chargedBy", e.target.value)
                        }
                      >
                        <option value="">{titleCase(t("select type"))}</option>
                        <option value="hourly">{titleCase(t("hourly"))}</option>
                        <option value="flat_fee">{titleCase(t("flat fee"))}</option>
                      </Input>
                    </FormGroup>
                  </>
                )}
              </CardBody>
            </Card>

            {/* ================= BOOKING SETTINGS ================= */}
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("booking settings"))}</H5>
              </CardHeader>
              <CardBody>
                <Row className="mb-3">
                  <Col md="6">
                    <Media className="align-items-center">
                      <Label className="switch m-r-10 mb-0">
                        <Input
                          type="checkbox"
                          checked={form.allowPublicBooking}
                          onChange={(e) =>
                            handleChange(
                              "allowPublicBooking",
                              e.target.checked
                            )
                          }
                        />
                        <span className="switch-state"></span>
                      </Label>
                      <Label>{titleCase(t("allow public booking"))}</Label>
                    </Media>
                  </Col>

                  <Col md="6">
                    <Media className="align-items-center">
                      <Label className="switch m-r-10 mb-0">
                        <Input
                          type="checkbox"
                          checked={form.requiresApproval}
                          onChange={(e) =>
                            handleChange(
                              "requiresApproval",
                              e.target.checked
                            )
                          }
                        />
                        <span className="switch-state"></span>
                      </Label>
                      <Label>{titleCase(t("requires approval"))}</Label>
                    </Media>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <Label>{titleCase(t("min lead time"))}</Label>
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={form.minLeadTimeValue}
                          onChange={(e) =>
                            handleChange("minLeadTimeValue", e.target.value)
                          }
                        />
                      </Col>
                      <Col md="6">
                        <Input
                          type="select"
                          value={form.minLeadTimeType}
                          onChange={(e) =>
                            handleChange("minLeadTimeType", e.target.value)
                          }
                        >
                          <option value="">
                            {titleCase(t("type"))}
                          </option>
                          <option value="day">
                            {titleCase(t("day"))}
                          </option>
                          <option value="month">
                            {titleCase(t("month"))}
                          </option>
                          <option value="year">
                            {titleCase(t("year"))}
                          </option>
                        </Input>
                      </Col>
                    </Row>
                    <small className="text-muted">
                      {t("min lead time explanation")}
                    </small>
                  </Col>

                  <Col md="6">
                    <Label>{titleCase(t("max lead time"))}</Label>
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={form.maxLeadTimeValue}
                          onChange={(e) =>
                            handleChange("maxLeadTimeValue", e.target.value)
                          }
                        />
                      </Col>
                      <Col md="6">
                        <Input
                          type="select"
                          value={form.maxLeadTimeType}
                          onChange={(e) =>
                            handleChange("maxLeadTimeType", e.target.value)
                          }
                        >
                          <option value="">
                            {titleCase(t("type"))}
                          </option>
                          <option value="day">
                            {titleCase(t("day"))}
                          </option>
                          <option value="month">
                            {titleCase(t("month"))}
                          </option>
                          <option value="year">
                            {titleCase(t("year"))}
                          </option>
                          <option value="month_end">
                            {titleCase(t("next n month end"))}
                          </option>
                          <option value="year_end">
                            {titleCase(t("next n year end"))}
                          </option>
                        </Input>
                      </Col>
                    </Row>
                    <small className="text-muted">
                      {t("max lead time explanation")}
                    </small>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* ================= OPERATING HOURS ================= */}
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("operating hours"))}</H5>
              </CardHeader>
              <CardBody>
                {form.operatingHours.map((oh, index) => (
                  <Row key={index} className="align-items-end mb-2">
                    <Col md="3">
                      <Input
                        type="select"
                        value={oh.day}
                        onChange={(e) =>
                          handleOperatingHourChange(index, "day", e.target.value)
                        }
                      >
                        <option value="">{titleCase(t("select day"))}</option>
                        <option value="Monday">{titleCase(t("monday"))}</option>
                        <option value="Tuesday">{titleCase(t("tuesday"))}</option>
                        <option value="Wednesday">{titleCase(t("wednesday"))}</option>
                        <option value="Thursday">{titleCase(t("thursday"))}</option>
                        <option value="Friday">{titleCase(t("friday"))}</option>
                        <option value="Saturday">{titleCase(t("saturday"))}</option>
                        <option value="Sunday">{titleCase(t("sunday"))}</option>
                      </Input>
                    </Col>

                    <Col md="3">
                      <Input
                        type="select"
                        value={oh.startTime}
                        onChange={(e) =>
                          handleOperatingHourChange(index, "startTime", e.target.value)
                        }
                      >
                        <option value="">{titleCase(t("start time"))}</option>
                        {generateTimeOptions().map((time) => (
                          <option key={time} value={time}>
                            {to12Hour(time)}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md="3">
                      <Input
                        type="select"
                        value={oh.endTime}
                        onChange={(e) =>
                          handleOperatingHourChange(index, "endTime", e.target.value)
                        }
                      >
                        <option value="">{titleCase(t("end time"))}</option>
                        {generateTimeOptions().map((time) => (
                          <option key={time} value={time}>
                            {to12Hour(time)}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md="3">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeOperatingHour(index)}
                      >
                        {titleCase(t("remove"))}
                      </button>
                      {index === form.operatingHours.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-2"
                          onClick={addOperatingHour}
                        >
                          {titleCase(t("add"))}
                        </button>
                      )}
                    </Col>
                  </Row>
                ))}
              </CardBody>
            </Card>

            {/* ================= DEVICE ================= */}
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("device connection"))}</H5>
              </CardHeader>
              <CardBody>
                <Row>
                  {devices.map((device) => (
                    <Col md="4" key={device.id} className="mb-2">
                      <Media className="align-items-center">
                        <Label className="switch m-r-10 mb-0">
                          <Input
                            type="checkbox"
                            checked={form.deviceIds.includes(device.id)}
                            onChange={() => toggleDevice(device.id)}
                          />
                          <span className="switch-state"></span>
                        </Label>
                        <div>
                          <div>{device.name}</div>
                          <small className="text-muted">
                            {sentenceCase(
                              t(device.type?.replace(/_/g, " "))
                            )}
                          </small>
                        </div>
                      </Media>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>

            {/* ================= REMARKS ================= */}
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("remarks"))}</H5>
              </CardHeader>
              <CardBody>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  wrapperStyle={{
                    border: "solid 0.5px #d9d9d9",
                    minHeight: "40vh",
                  }}
                  editorStyle={{
                    paddingLeft: "15px",
                    paddingRight: "15px",
                  }}
                />
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default VenueEdit;